import { useEffect, useState, useCallback } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Loader2, RefreshCw, AlertTriangle, Download, ChevronLeft, ChevronRight } from 'lucide-react'
import { api } from '@/services/api'

const PAGE_SIZE = 50

interface SniperFlags {
  SNIPER_ENGINE_ENABLED: boolean
  SNIPER_DISPATCH_ENABLED: boolean
}

interface SniperCounters {
  evaluated: number
  no_signal: number
  allowed: number
  blocked: number
  logged: number
  dedup_skipped: number
  conflict_with_yomi: number
  last_decision: any
}

interface SniperLiveStats {
  flags: SniperFlags
  config: any
  counters: SniperCounters
}

interface SniperRow {
  id: number
  created_at: string
  m5_candle_ts: string | null
  decision: string
  authorized: boolean
  reason: string | null
  direction: string | null
  scenario: string | null
  score: number | null
  signal_label: string | null
  liquidity_sweep: { hit: boolean; side: string | null; level: number | null }
  rejection_candle: { hit: boolean; kind: string | null }
  volume_spike: { hit: boolean; z_score: number | null }
  bb_extreme: { hit: boolean; position_pct: number | null }
  adx_m5: number | null
  reference_price: number | null
  yomi_conflict: boolean
  yomi_direction: string | null
  yomi_scenario: string | null
}

interface SniperStats {
  window_days: number
  total: number
  allowed: number
  blocked: number
  conflicts_with_yomi: number
  by_scenario: Record<string, number>
  by_label: Record<string, number>
}

const fmtDate = (iso: string | null) => {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    })
  } catch { return iso }
}

const dirCls = (d: string | null) =>
  d === 'BUY' ? 'text-sky-400' : d === 'SELL' ? 'text-rose-400' : 'text-slate-400'

const labelBadge = (l: string | null): string => {
  switch ((l ?? '').toUpperCase()) {
    case 'PREMIUM':  return 'bg-blue-500/15 text-blue-400 border-blue-500/30'
    case 'STANDARD': return 'bg-sky-500/15 text-sky-400 border-sky-500/30'
    case 'CAUTION':  return 'bg-amber-500/15 text-amber-400 border-amber-500/30'
    default:         return 'bg-slate-500/15 text-slate-400 border-slate-500/30'
  }
}

const decisionBadge = (d: string) =>
  d === 'ALLOW' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
  : 'bg-red-500/15 text-red-400 border-red-500/30'

export const SniperLog = () => {
  const [live, setLive] = useState<SniperLiveStats | null>(null)
  const [stats, setStats] = useState<SniperStats | null>(null)
  const [rows, setRows] = useState<SniperRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [conflicts, setConflicts] = useState<SniperRow[]>([])
  const [onlyAllowed, setOnlyAllowed] = useState(false)
  const [onlyConflicts, setOnlyConflicts] = useState(false)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [exportingJson, setExportingJson] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset page quando cambiano i filtri
  useEffect(() => { setPage(0) }, [onlyAllowed, onlyConflicts])

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const offset = page * PAGE_SIZE
      const [l, s, r, c] = await Promise.all([
        api.get<SniperLiveStats>('/admin/sniper/live-stats'),
        api.get<SniperStats>('/admin/sniper/stats?days=7'),
        api.get<{ rows: SniperRow[]; count: number; total: number }>(
          `/admin/sniper/recent-signals?limit=${PAGE_SIZE}&offset=${offset}&only_allowed=${onlyAllowed}&only_conflicts=${onlyConflicts}`),
        api.get<{ rows: SniperRow[]; count: number }>(
          '/admin/sniper/conflicts?minutes=60'),
      ])
      setLive(l); setStats(s); setRows(r.rows); setTotal(r.total ?? 0); setConflicts(c.rows)
    } catch (e: any) {
      setError(e?.message || 'Errore caricamento Sniper log')
    } finally {
      setLoading(false)
    }
  }, [onlyAllowed, onlyConflicts, page])

  useEffect(() => { fetchAll() }, [fetchAll])
  useEffect(() => {
    // Auto-refresh solo sulla prima pagina per non disturbare la navigazione storica
    if (page !== 0) return
    const t = setInterval(fetchAll, 15000)
    return () => clearInterval(t)
  }, [fetchAll, page])

  const exportCsv = useCallback(async () => {
    setExporting(true); setError(null)
    try {
      const authData = localStorage.getItem('auth-storage')
      let token: string | null = null
      if (authData) {
        try { token = JSON.parse(authData).state?.token ?? null } catch { /* ignore */ }
      }
      const params = new URLSearchParams({
        only_allowed: String(onlyAllowed),
        only_conflicts: String(onlyConflicts),
      })
      const resp = await fetch(`/api/v1/admin/sniper/export-csv?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!resp.ok) throw new Error(`Export fallito (HTTP ${resp.status})`)
      const blob = await resp.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const ts = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 19)
      a.download = `sniper_signals_${ts}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (e: any) {
      setError(e?.message || 'Errore export CSV')
    } finally {
      setExporting(false)
    }
  }, [onlyAllowed, onlyConflicts])

  const exportJson = useCallback(async () => {
    setExportingJson(true); setError(null)
    try {
      const authData = localStorage.getItem('auth-storage')
      let token: string | null = null
      if (authData) {
        try { token = JSON.parse(authData).state?.token ?? null } catch { /* ignore */ }
      }
      const params = new URLSearchParams({
        only_allowed: String(onlyAllowed),
        only_conflicts: String(onlyConflicts),
      })
      const resp = await fetch(`/api/v1/admin/sniper/export-json?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!resp.ok) throw new Error(`Export fallito (HTTP ${resp.status})`)
      const blob = await resp.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const ts = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 19)
      a.download = `hyoshi_export_${ts}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (e: any) {
      setError(e?.message || 'Errore export JSON')
    } finally {
      setExportingJson(false)
    }
  }, [onlyAllowed, onlyConflicts])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const fromIdx = total === 0 ? 0 : page * PAGE_SIZE + 1
  const toIdx = Math.min(total, page * PAGE_SIZE + rows.length)

  const engineOn = !!live?.flags.SNIPER_ENGINE_ENABLED
  const dispatchOn = !!live?.flags.SNIPER_DISPATCH_ENABLED

  return (
    <div className="space-y-4">
      {/* Flag status */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground">Engine</CardTitle></CardHeader>
          <CardContent>
            <Badge className={engineOn ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' : 'bg-slate-500/15 text-slate-400 border-slate-500/30'}>
              {engineOn ? 'ENABLED' : 'DISABLED'}
            </Badge>
            <div className="text-[11px] text-muted-foreground mt-1">SNIPER_ENGINE_ENABLED</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground">Dispatch</CardTitle></CardHeader>
          <CardContent>
            <Badge className={dispatchOn ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' : 'bg-slate-500/15 text-slate-400 border-slate-500/30'}>
              {dispatchOn ? 'ENABLED' : 'DISABLED'}
            </Badge>
            <div className="text-[11px] text-muted-foreground mt-1">SNIPER_DISPATCH_ENABLED</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground">Conflitti Yomi</CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-400">{conflicts.length}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">pendenti (60min)</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground">Azioni</CardTitle></CardHeader>
          <CardContent>
            <Button size="sm" variant="outline" onClick={fetchAll} disabled={loading}>
              <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`}/>
              Refresh
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card><CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground">Evaluated</CardTitle></CardHeader><CardContent><div className="text-xl font-bold">{live?.counters.evaluated ?? '—'}</div></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-xs text-emerald-500">ALLOWED</CardTitle></CardHeader><CardContent><div className="text-xl font-bold text-emerald-400">{live?.counters.allowed ?? '—'}</div></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-xs text-red-400">BLOCKED</CardTitle></CardHeader><CardContent><div className="text-xl font-bold text-red-400">{live?.counters.blocked ?? '—'}</div></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground">Logged DB</CardTitle></CardHeader><CardContent><div className="text-xl font-bold">{live?.counters.logged ?? '—'}</div></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground">Dedup skip</CardTitle></CardHeader><CardContent><div className="text-xl font-bold">{live?.counters.dedup_skipped ?? '—'}</div></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-xs text-amber-400">Conflict Yomi</CardTitle></CardHeader><CardContent><div className="text-xl font-bold text-amber-400">{live?.counters.conflict_with_yomi ?? '—'}</div></CardContent></Card>
      </div>

      {/* Stats 7d */}
      {stats && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Statistiche ultimi 7 giorni</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><span className="text-muted-foreground">Totale: </span><b>{stats.total}</b></div>
              <div><span className="text-muted-foreground">Allowed: </span><b className="text-emerald-400">{stats.allowed}</b></div>
              <div><span className="text-muted-foreground">Conflitti: </span><b className="text-amber-400">{stats.conflicts_with_yomi}</b></div>
              <div><span className="text-muted-foreground">Scenari: </span><b>{Object.keys(stats.by_scenario).length}</b></div>
            </div>
            {Object.keys(stats.by_scenario).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {Object.entries(stats.by_scenario).map(([k, v]) => (
                  <Badge key={k} variant="outline">{k || 'NONE'}: {v}</Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Conflitti pendenti */}
      {conflicts.length > 0 && (
        <Card className="border-amber-500/30">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-400" />
              Conflitti Yomi ↔ Sniper pendenti ({conflicts.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {conflicts.slice(0, 5).map(r => (
                <div key={r.id} className="flex items-center justify-between text-sm border border-amber-500/20 rounded p-2 bg-amber-500/5">
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">{fmtDate(r.created_at)}</span>
                    <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 text-xs">CONFLITTO</Badge>
                  </div>
                  <div className="text-xs">
                    <span className="text-muted-foreground">Yomi: </span>
                    <span className={dirCls(r.yomi_direction)}>{r.yomi_direction}</span>
                    <span className="text-muted-foreground ml-1">({r.yomi_scenario})</span>
                    <span className="mx-2">vs</span>
                    <span className="text-muted-foreground">Sniper: </span>
                    <span className={dirCls(r.direction)}>{r.direction}</span>
                    <span className="text-muted-foreground ml-1">({r.scenario} · {r.score?.toFixed(2)})</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    @{r.reference_price?.toFixed(2) ?? '—'}
                  </div>
                </div>
              ))}
              <div className="text-xs text-muted-foreground italic mt-2">
                In questa fase l'utente decide manualmente: nessun dispatch automatico in caso di conflitto.
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Toggles + tabella */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-sm">
              Segnali Sniper
              {total > 0 && (
                <span className="ml-2 text-xs text-muted-foreground font-normal">
                  {fromIdx}–{toIdx} di {total}
                </span>
              )}
            </CardTitle>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant={onlyAllowed ? 'default' : 'outline'} onClick={() => setOnlyAllowed(v => !v)}>Solo ALLOW</Button>
              <Button size="sm" variant={onlyConflicts ? 'default' : 'outline'} onClick={() => setOnlyConflicts(v => !v)}>Solo conflitti</Button>
              <Button size="sm" variant="outline" onClick={exportCsv} disabled={exporting} title="Esporta i segnali filtrati in CSV (max 10k righe)">
                {exporting
                  ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  : <Download className="h-3 w-3 mr-1" />}
                Export CSV
              </Button>
              <Button size="sm" variant="outline" onClick={exportJson} disabled={exportingJson} title="Esporta i segnali filtrati nello schema JSON unico (max 10k righe)">
                {exportingJson
                  ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  : <Download className="h-3 w-3 mr-1" />}
                Export JSON
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error && <div className="text-red-400 text-sm mb-2">{error}</div>}
          {loading && rows.length === 0 ? (
            <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="h-4 w-4 animate-spin" />Caricamento…</div>
          ) : rows.length === 0 ? (
            <div className="text-muted-foreground text-sm py-6 text-center">
              Nessun segnale Sniper ancora loggato.
              {!engineOn && <div className="mt-1 text-xs">Engine disabilitato — setta <code>SNIPER_ENGINE_ENABLED=true</code> per iniziare a generare segnali.</div>}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-muted-foreground border-b">
                    <th className="py-2 pr-2">Quando</th>
                    <th className="pr-2">Decisione</th>
                    <th className="pr-2">Dir</th>
                    <th className="pr-2">Scenario</th>
                    <th className="pr-2">Score</th>
                    <th className="pr-2">Label</th>
                    <th className="pr-2">Sweep</th>
                    <th className="pr-2">Rej</th>
                    <th className="pr-2">Vol Z</th>
                    <th className="pr-2">BB pos</th>
                    <th className="pr-2">@</th>
                    <th className="pr-2">Yomi</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.id} className={`border-b border-slate-800/50 ${r.yomi_conflict ? 'bg-amber-500/5' : ''}`}>
                      <td className="py-1.5 pr-2 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                      <td className="pr-2"><Badge className={`${decisionBadge(r.decision)} text-[10px]`}>{r.decision}</Badge></td>
                      <td className={`pr-2 font-mono ${dirCls(r.direction)}`}>{r.direction ?? '—'}</td>
                      <td className="pr-2">{r.scenario ?? '—'}</td>
                      <td className="pr-2 font-mono">{r.score?.toFixed(3) ?? '—'}</td>
                      <td className="pr-2"><Badge className={`${labelBadge(r.signal_label)} text-[10px]`}>{r.signal_label ?? '—'}</Badge></td>
                      <td className="pr-2">{r.liquidity_sweep.hit ? <span className="text-emerald-400">{r.liquidity_sweep.side}</span> : '—'}</td>
                      <td className="pr-2">{r.rejection_candle.hit ? <span className="text-emerald-400">{r.rejection_candle.kind}</span> : '—'}</td>
                      <td className="pr-2 font-mono">{r.volume_spike.z_score?.toFixed(2) ?? '—'}</td>
                      <td className="pr-2 font-mono">{r.bb_extreme.position_pct?.toFixed(2) ?? '—'}</td>
                      <td className="pr-2 font-mono">{r.reference_price?.toFixed(2) ?? '—'}</td>
                      <td className="pr-2">
                        {r.yomi_conflict ? (
                          <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 text-[10px]">⚠ {r.yomi_direction}</Badge>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Paginazione */}
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-800/50">
              <div className="text-xs text-muted-foreground">
                Pagina {page + 1} di {totalPages}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0 || loading}
                >
                  <ChevronLeft className="h-3 w-3 mr-1" />Prec
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1 || loading}
                >
                  Succ<ChevronRight className="h-3 w-3 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default SniperLog
